import { z } from "zod";

import type { MerchantCouponUIState } from "../data/merchantCouponEditor.data";

const requiredDateSchema = (message: string) =>
  z
    .string()
    .nullable()
    .refine((value) => value !== null && !Number.isNaN(Date.parse(value)), { message });

export const merchantCouponEditorSchema = z
  .object({
    coupon_title: z.string().trim().min(1, "Coupon title is required"),
    description: z.string().trim().min(1, "Description is required"),
    discount_type: z.enum(["percentage", "fixed_amount"], {
      message: "Discount type is required",
    }),
    discount_value: z.number().gt(0, "Discount value must be greater than 0"),
    total_quantity_available: z.number().gt(0, "Total quantity available must be greater than 0"),
    start_date: requiredDateSchema("Start date is required"),
    end_date: requiredDateSchema("End date is required"),
    limit_per_user: z.number().gt(0, "Limit per user must be greater than 0"),
    limit_per_day: z.number().gt(0, "Limit per day must be greater than 0").nullable().optional(),
    points_required: z.number().min(0, "Points required cannot be negative"),
    terms_and_conditions: z
      .array(z.string())
      .min(1, "At least one term and condition is required")
      .refine(
        (terms) => terms.some((term) => term.trim().length > 0),
        "At least one term and condition is required",
      ),
  })
  .superRefine((data, context) => {
    if (data.discount_type === "percentage" && data.discount_value > 100) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["discount_value"],
        message: "Percentage discount cannot exceed 100%",
      });
    }

    if (
      data.start_date &&
      data.end_date &&
      Date.parse(data.start_date) <= Date.parse(data.end_date)
    ) {
      return;
    }

    if (data.start_date && data.end_date) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end_date"],
        message: "End date must be after start date",
      });
    }
  });

export type MerchantCouponValidationErrors = Partial<
  Record<keyof MerchantCouponUIState, string>
>;

export interface MerchantCouponValidationResult {
  isValid: boolean;
  errors: string[];
  fieldErrors: MerchantCouponValidationErrors;
  firstError?: string;
}

export const validateMerchantCouponSchema = (
  coupon: MerchantCouponUIState,
): MerchantCouponValidationResult => {
  const result = merchantCouponEditorSchema.safeParse(coupon);

  if (result.success) {
    return { isValid: true, errors: [], fieldErrors: {} };
  }

  const errors = result.error.issues.map((issue) => issue.message);
  const fieldErrors: MerchantCouponValidationErrors = {};

  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !(field in fieldErrors)) {
      fieldErrors[field as keyof MerchantCouponUIState] = issue.message;
    }
  }

  return {
    isValid: false,
    errors,
    fieldErrors,
    firstError: errors[0],
  };
};
